import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type InputGroupButtonRadiusConstantType,
    type InputGroupButtonColorsConstantType,
    type InputGroupButtonDynamicStyleType,
    type InputGroupButtonColors,
    type InputGroupButtonType
} from "./type";
import {
    getTypeColors
} from "../../../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../../../types";
import {
    type StateColorKey,
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    concentric
} from "../../../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    webStyle
} from "../../../../utils";

export const INPUT_GROUP_BUTTON_OVERLAY_COLORS: Record<InputGroupButtonType, StateColorKey> = {
    primary: "primary",
    neutral: "default"
};

export const getInputGroupButtonColors = ({
    isHighlighted,
    isIconAction,
    isDisabled,
    isSelected,
    colors,
    type
}: InputGroupButtonColorsConstantType): InputGroupButtonColors => {
    if(isDisabled) {
        return {
            containerColor: isSelected ? colors.system.state.overlay.disabled.neutral : undefined,
            borderColor: isSelected ? colors.system.state.border.disabled.neutral : undefined,
            titleColor: colors.system.state.content.disabled.neutral,
            iconColor: colors.system.state.content.disabled.neutral,
            overlayColor: INPUT_GROUP_BUTTON_OVERLAY_COLORS[type],
            iconColorKey: "disabled"
        };
    }

    if(isSelected) {
        const selectedColors = getTypeColors({
            isSelected: true,
            variant: "ghost",
            type: "neutral",
            colors
        });

        return {
            containerColor: selectedColors.containerColor,
            overlayColor: selectedColors.overlayColor,
            titleColor: selectedColors.titleColor,
            iconColor: selectedColors.iconColor,
            iconColorKey: "high"
        };
    }

    const isHighlightedIcon = isIconAction && isHighlighted;

    return {
        titleColor: type === "primary" ? colors.content.text.emphasized : colors.content.text.high,
        iconColor: isHighlightedIcon ? colors.content.icon.default : colors.content.icon.mid,
        overlayColor: INPUT_GROUP_BUTTON_OVERLAY_COLORS[type],
        iconColorKey: isHighlightedIcon ? "default" : "mid"
    };
};

export const getInputGroupButtonRadius = ({
    isIconAction,
    radiuses,
    borders,
    spaces
}: InputGroupButtonRadiusConstantType): number => {
    return concentric({
        role: isIconAction ? radiuses.xs : radiuses.actions,
        inset: spaces.spacingXs + borders.line,
        outer: radiuses.form
    });
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0,
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    surface: {
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center",
        outlineOffset: 0
    }
});

export const useStyles = ({
    isFocusVisible,
    currentColors,
    isIconAction,
    shapeConfigs,
    touchTargets,
    currentSize,
    pointerType,
    radiuses,
    borders,
    colors,
    spaces
}: InputGroupButtonDynamicStyleType) => {
    const borderRadius = getInputGroupButtonRadius({
        isIconAction,
        radiuses,
        borders,
        spaces
    });

    const textHeight = currentSize.minHeight - spaces.spacingXs * 2;

    const textHitHeight = pointerType === "coarse" ? currentSize.minHeight : Math.max(textHeight, touchTargets.fine);

    const styles = {
        container: {
            marginHorizontal: (currentSize.iconSize - currentSize.actionHitSize) / 2,
            marginVertical: (currentSize.actionSize - currentSize.actionHitSize) / 2,
            height: currentSize.actionHitSize,
            width: currentSize.actionHitSize
        } as Mutable<ViewStyle>,
        surface: {
            height: currentSize.actionSize,
            width: currentSize.actionSize
        } as Mutable<ViewStyle>
    };

    Object.assign(styles.surface, getCornerStyle({
        radius: borderRadius,
        shapeConfigs
    }));

    if(!isIconAction) {
        styles.container = {
            marginVertical: (textHeight - textHitHeight) / 2,
            minHeight: textHitHeight
        };

        styles.surface = {
            paddingHorizontal: spaces.spacingSm,
            gap: spaces.spacingXs,
            height: textHeight,
            ...getCornerStyle({
                radius: borderRadius,
                shapeConfigs
            })
        };
    }

    if(currentColors.containerColor) {
        styles.surface.backgroundColor = currentColors.containerColor;
    }

    if(currentColors.borderColor) {
        styles.surface.borderColor = currentColors.borderColor;

        styles.surface.borderWidth = borders.line;
    }

    Object.assign(styles.surface, getFocusRingStyle({
        colorKey: "default",
        isFocusVisible,
        borders,
        colors
    }));

    delete styles.surface.outlineOffset;

    return styles;
};
export default stylesheet;
